import {
  useState,
  ChangeEvent,
  useCallback,
  useEffect,
  SyntheticEvent,
} from "react";
import styled from "styled-components";
import { useAutoFocus } from "../../hooks/useAutoFocus";
import {
  Availability,
  ScreenNames,
  ScreenProps,
} from "../../types/ScreenTypes";
import {
  Wrapper,
  CallButton,
  LinkButton,
  KeypadInput,
  Row,
  Button,
  FromNumberRow,
  Input,
} from "../Components";
import { Keypad } from "../Keypad";
import { StartCallSvg, DeleteLeftSvg } from "../Icons";
import { GREAT_WHITE } from "../../utils/colors";
import FromNumbersDropdown from "../FromNumbersDropdown";
import AvailabilityDropdown from "../AvailabilityDropdown";
import {
  validateKeypadInput,
  validatePhoneNumber,
} from "../../utils/phoneNumberUtils";
import { LOG_OUT, OUTGOING_CALL } from "../../constants/buttonIds";
import axios from "axios";
import { GetLocalStorage, RemoveLocalStorage, urls } from "../api/api";
import { io, Socket } from "socket.io-client";

const StyledRow = styled(Row)`
  justify-content: space-between;
`;

function KeypadScreen({
  handleNextScreen,
  cti,
  dialNumber,
  setDialNumber,
  handleNavigateToScreen,
  startTimer,
  fromNumber,
  setFromNumber,
  availability,
  setAvailability,
  setDirection,
  incomingNumber,
  setIncomingNumber,
  handleIncomingCall,
  handleOutgoingCallStarted,
}: ScreenProps) {
  const dialNumberInput = useAutoFocus();
  const [cursorStart, setCursorStart] = useState(dialNumber.length || 0);
  const [cursorEnd, setCursorEnd] = useState(dialNumber.length || 0);
  const [isDialNumberValid, setIsDialNumberValid] = useState(false);
  const [toggleFromNumbers, setToggleFromNumbers] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  setTimeout(() => {
    const storedNumber = localStorage.getItem("usernumber")?.trim();
    if (storedNumber) {
      const last10Digits =
        storedNumber.length >= 10 ? storedNumber.slice(-10) : storedNumber;
      setIsDialNumberValid(validatePhoneNumber(last10Digits));
      setDialNumber(`+91${last10Digits}`);
    }
  }, 100);

  const userInformation = GetLocalStorage("userInformation");
  if (!userInformation) {
    throw new Error("User information not found.");
  }
  const parsedUserInfo = JSON.parse(userInformation);
  const handleLogout = async () => {
    const Form = new FormData();
    Form.append("authcode", parsedUserInfo.authcode);
    Form.append("agent_status", "2");
    axios
      .post(urls.status, Form)
      .then((response) => {
        RemoveLocalStorage("userInformation");
        cti.userLoggedOut();
        handleNavigateToScreen(ScreenNames.Login);
      })
      .catch((error) => {
        console.error("Error updating availability status", error);
      });
  };

  const handleTriggerIncomingCall = useCallback(
    (callData: { SourceNumber: string; DestinationNumber: string }) => {
      handleIncomingCall();
    },
    [cti, fromNumber, handleIncomingCall]
  );

  useEffect(() => {
    const socket: Socket = io(`${urls.socket}`, {
      path: "/ws/hubspot",
      transports: ["websocket"],
    });

    socket.on("connect", () => {
      console.log("Connected to hubspot socket.io server");
    });

    socket.on("disconnect", () => {
      console.log("Disconnected from hubspot socket.io KeypadScreen");
    });

    const handleNewCall = (payload: any) => {
      const data = payload.payload;
      if (
        data.Status === "Transferring Call to Agent" &&
        data.Direction === "IVR" &&
        data.DialWhomNumber.slice(-10) === parsedUserInfo.mobile_no.slice(-10)
      ) {
        const IncomingNumber = data.SourceNumber.replace(/^0+/, "").trim();
        setIncomingNumber(`+91 ${IncomingNumber}`);
        const DestinationNumber = data.DestinationNumber.replace(
          /^0+/,
          ""
        ).trim();
        handleTriggerIncomingCall({
          SourceNumber: `+91${IncomingNumber}`,
          DestinationNumber: `+91${DestinationNumber}`,
        });
      }else if(data.Direction==="WEBOBD" && data.Status === "Transferring Call to Agent" &&
          data.DialWhomNumber.slice(-10) === parsedUserInfo.mobile_no.slice(-10)
      ){
        handleOutgoingCall(data.SourceNumber);
      }
    };
    socket.on("message", handleNewCall);
    return () => {
      socket.off("message", handleNewCall);
      socket.disconnect();
    };
  }, [parsedUserInfo.mobile_no, handleTriggerIncomingCall]);

  const handleCursor = ({
    target: { selectionStart, selectionEnd },
  }: ChangeEvent<HTMLInputElement>) => {
    setCursorStart(selectionStart || 0);
    setCursorEnd(selectionEnd || 0);
  };

  const handleDialNumber = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    if (value.startsWith("+91") || value.length === 0) {
      setErrorMessage(null);
    } else {
      setErrorMessage("Oops! Please make sure to keep the +91 country code.");
      return;
    }

    const newValue = value.startsWith("+91") ? value : "+91" + value.slice(3);
    if (validateKeypadInput(newValue)) {
      setDialNumber(newValue);
      setErrorMessage(null);
    } else {
      setErrorMessage("Invalid input. Please enter a valid phone number.");
    }
  };

  const addToDialNumber = (value: string) => {
    setDialNumber(dialNumber + value);
    dialNumberInput.current?.focus();
  };

  const handleOutgoingCall = useCallback(async (webHookOutGoingNumber:string) => {
    try {
      const mobileNumber = parsedUserInfo.mobile_no.replace(/^0+/, "").trim();
      let OutGoingNumber = dialNumber.replace(/^\+91/, "").trim().replace(/^0+/, "").trim();
      if(webHookOutGoingNumber){
        OutGoingNumber = webHookOutGoingNumber.replace(/^\+91/, "").trim().replace(/^0+/, "").trim()
      }
      setDialNumber(`+91 ${OutGoingNumber}`)
      setIncomingNumber(`+91 ${OutGoingNumber}`)

      if (OutGoingNumber.length !== 10) {
        setErrorMessage("Please enter a valid 10-digit number.");
        return;
      }

      const response = await axios.get(`${urls.clickToCall}`, {
        params: {
          calling_party_a: mobileNumber,
          calling_party_b: OutGoingNumber,
          deskphone:
            "0" +
            parsedUserInfo.default_did
              .replace(/\D/g, "")
              .replace(/^0+/, "")
              .slice(-10),
          authcode: parsedUserInfo.authcode,
          call_from_did: 1,
        },
      });

      if (response?.data?.type === "error") {
        setErrorMessage(response.data.message);
        return;
      }
      localStorage.setItem("outgoing", "true");
      sessionStorage.setItem("livedata", response.data.campid);
      // const callStartTime = Date.now();
      cti.outgoingCall({
        // createEngagement: true,
        toNumber: `+91${OutGoingNumber}`,
        fromNumber: `+91${parsedUserInfo.default_did.slice(-10)}`,
        // callStartTime,
      });
      handleOutgoingCallStarted();

      handleIncomingCall();
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage("Failed to start the call. Please try again.");
    }
  }, [cti, dialNumber, fromNumber, handleOutgoingCallStarted]);

  const handleBackspace = useCallback(() => {
    let updatedToNumber =
      dialNumber.substring(0, cursorStart) + dialNumber.substring(cursorEnd);

    if (dialNumber.startsWith("+91")) {
      if (cursorStart <= 3) {
        setErrorMessage("Oops! Please make sure to keep the +91 country code.");
        updatedToNumber = "+91" + dialNumber.substring(3);
      } else {
        updatedToNumber =
          dialNumber.substring(0, cursorStart - 1) +
          dialNumber.substring(cursorEnd);
      }
    } else if (cursorStart === cursorEnd) {
      updatedToNumber =
        dialNumber.substring(0, cursorStart - 1) +
        dialNumber.substring(cursorEnd);
    }

    setDialNumber(updatedToNumber);

    if (dialNumberInput.current) {
      dialNumberInput.current.value = updatedToNumber;
      dialNumberInput.current.setSelectionRange(cursorStart, cursorStart);
      dialNumberInput.current.focus();
    }
  }, [cursorEnd, cursorStart, dialNumber, dialNumberInput, setDialNumber]);

  const handleSetAvailability = useCallback(
    (availability: Availability) => {
      if (availability === "AVAILABLE") {
        cti.userAvailable();
      } else {
        cti.userUnavailable();
      }
      setAvailability(availability);
    },
    [setAvailability, cti]
  );

  return (
    <Wrapper>
      <StyledRow>
        <AvailabilityDropdown
          availability={availability}
          setAvailability={handleSetAvailability}
          triggerIncomingCall={handleTriggerIncomingCall}
          incomingNumber={incomingNumber}
          setIncomingNumber={setIncomingNumber}
        />
        <LinkButton onClick={handleLogout} data-test-id={LOG_OUT}>
          Log out
        </LinkButton>
      </StyledRow>
      <br />
      {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
      <div
        style={{
          display: "flex",
          justifyContent: "space-evenly",
          backgroundColor: "white",
          border: `2px solid ${GREAT_WHITE}`,
          margin: "0 16px",
          padding: "4px",
        }}
      >
        <KeypadInput
          data-test-id="dial-number-input"
          value={dialNumber}
          onChange={handleDialNumber}
          onBlur={handleCursor}
          ref={dialNumberInput}
        />
        <Button aria-label="backspace" onClick={handleBackspace}>
          {DeleteLeftSvg}
        </Button>
      </div>
      <br />
      <Keypad addToDialNumber={addToDialNumber} />
      <Row>
        <CallButton
          use="primary"
          disabled={!isDialNumberValid}
          onClick={()=>handleOutgoingCall(dialNumber)}
          aria-label="outgoing-call"
          data-test-id={OUTGOING_CALL}
        >
          {StartCallSvg}
        </CallButton>
      </Row>
      <FromNumberRow>
        <FromNumbersDropdown
          fromNumber={fromNumber}
          setFromNumber={setFromNumber}
          toggleFromNumbers={toggleFromNumbers}
          setToggleFromNumbers={setToggleFromNumbers}
        />
      </FromNumberRow>
    </Wrapper>
  );
}

export default KeypadScreen;
